import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import Sortable from "sortablejs";

import {
  FormFieldRenderer,
  evalCondition,
} from "../components/FormFieldRenderer";
import { Icon, type IconName } from "../components/Icon";
import { useModalDismiss } from "../hooks/useModalDismiss";
import { initialFormValues } from "../utils/formValues";
import {
  api,
  type AdminCompanyRow,
  type AutotaskFieldTarget,
  type ConditionalExpression,
  type FormAdmin,
  type FormFieldOption,
  type FormFieldSchema,
  type FormFieldType,
  type FormIconColor,
  type FormInternalNotifyConfig,
  type FormInternalNotifyRecipient,
  type FormSchemaShape,
  type FormScope,
  type FormSection,
  type Me,
} from "../services/api";

/**
 * Admin Form Builder (Brief v1.2 §7.4) — the approved "Online Forms" refinement
 * (design handoff §5.3 / §6 / §7). Three panels:
 *
 * - LEFT: every form, searchable, grouped by client-facing name (dual naming),
 *   click to switch.
 * - CENTER: a toolbar with the dual title editor (internal IT name + the name
 *   customers see) and undo/redo, then a tabbed builder (Fields / Logic /
 *   Styling / Integrations). The Fields canvas groups fields into sections with
 *   drag-to-reorder, a per-row half/full width switch (WYSIWYG of the runner),
 *   and IF / REQ / mapping badges.
 * - RIGHT: the selected field's properties — label first and largest, then
 *   General, Layout (width), Options, Behaviour (Required + Conditional display
 *   with an amber rule-builder), Validation, and Autotask mapping.
 *
 * Save mechanics are unchanged: all edits mutate local ``schema`` state through
 * ``commit`` (which feeds undo/redo); "Save draft" PATCHes the draft schema +
 * metadata; "Publish" saves the draft then POSTs publish behind a confirm.
 */

// ---- Field-type catalogue ---------------------------------------------------

const FIELD_TYPES: Array<{ type: FormFieldType; label: string; blurb: string }> = [
  { type: "short_text", label: "Short text", blurb: "Single-line text input." },
  { type: "long_text", label: "Long text", blurb: "Multi-line text input." },
  { type: "number", label: "Number", blurb: "Numeric input with bounds." },
  { type: "date", label: "Date", blurb: "Date picker (YYYY-MM-DD)." },
  { type: "datetime", label: "Date + time", blurb: "Date and time picker." },
  { type: "single_select", label: "Dropdown", blurb: "Pick one option." },
  { type: "multi_select", label: "Multi-select", blurb: "Pick many options." },
  { type: "radio_group", label: "Radio buttons", blurb: "One choice, visible." },
  { type: "checkbox_group", label: "Checkbox group", blurb: "Many choices, visible." },
  { type: "single_checkbox", label: "Checkbox", blurb: "Single yes/no." },
  { type: "file_upload", label: "File upload", blurb: "Attach one or more files." },
  { type: "user_picker", label: "User picker", blurb: "Reference a portal user." },
  { type: "section_header", label: "Section header", blurb: "Visual divider, no data." },
];

const TYPE_LABEL: Record<string, string> = Object.fromEntries(
  FIELD_TYPES.map((t) => [t.type, t.label]),
);

const ICON_COLORS: FormIconColor[] = ["violet", "green", "orange", "red", "blue"];

const UNGROUPED = "__ungrouped__";

type BuilderTab = "fields" | "logic" | "styling" | "integrations";

// Field types that drive a conditional rule (have a discrete value set, or a
// boolean in the single-checkbox case — e.g. "Does the user need a phone?").
const TRIGGER_TYPES: FormFieldType[] = [
  "single_select",
  "radio_group",
  "checkbox_group",
  "multi_select",
  "single_checkbox",
];
const MULTI_TRIGGER_TYPES: FormFieldType[] = ["checkbox_group", "multi_select"];

// A single checkbox has no options of its own; it submits a boolean. We give
// the rule builder one synthetic "ticked" value so "is" / "is not" read as
// "is ticked" / "is not ticked", and coerce it to a real boolean in the
// expression (the runtime compares with strict equality — see ruleToExpr).
const CHECKBOX_TRIGGER_OPTIONS: FormFieldOption[] = [{ value: "true", label: "ticked" }];

// A graceful fallback when the Autotask sync endpoint isn't live yet.
const FALLBACK_TARGETS: AutotaskFieldTarget[] = [
  { target: "title", label: "Title", kind: "text" },
  { target: "description", label: "Description", kind: "text" },
];

// ---- Root -------------------------------------------------------------------

export function AdminFormEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, mutate, error: loadErr } = useSWR<FormAdmin>(
    id ? `/api/admin/forms/${id}` : null,
    () => api.admin.forms.get(id as string),
  );
  const { data: allForms } = useSWR<FormAdmin[]>(
    "/api/admin/forms?all",
    () => api.admin.forms.list({ include_unpublished: true }),
  );
  const { data: companies } = useSWR<AdminCompanyRow[]>(
    "/api/admin/companies",
    () => api.admin.companies.list(),
  );
  const { data: me } = useSWR<Me>("/api/me", () => api.me());
  const { data: autotaskFields } = useSWR<AutotaskFieldTarget[]>(
    "/api/admin/forms/autotask-fields",
    async () => {
      try {
        const res = await api.admin.forms.autotaskFields();
        return res.targets;
      } catch {
        return FALLBACK_TARGETS;
      }
    },
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  // We fell back to the fixed list when the endpoint isn't live — surface a
  // hint so admins know picklist targets are unavailable until Autotask syncs.
  const autotaskDegraded =
    autotaskFields !== undefined &&
    autotaskFields.length === FALLBACK_TARGETS.length &&
    autotaskFields.every(
      (t) => t.target === "title" || t.target === "description",
    );

  const [schema, setSchema] = useState<FormSchemaShape | null>(null);
  const [title, setTitle] = useState("");
  const [clientTitle, setClientTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<BuilderTab>("fields");
  const [showPreview, setShowPreview] = useState(false);
  const [showCopyFrom, setShowCopyFrom] = useState(false);
  // The Forms list eats ~250px; collapsing it gives the builder canvas more
  // room on laptops. The preference persists per-device so it stays collapsed
  // while editing form to form (issue #17).
  const [showFormList, setShowFormList] = useState<boolean>(
    () => localStorage.getItem("rf-formlist-hidden") !== "1",
  );
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [scopeSaving, setScopeSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ---- Undo / redo history of schema snapshots -----------------------------
  const history = useRef<FormSchemaShape[]>([]);
  const historyPos = useRef(-1);
  const [, forceHistoryRender] = useState(0);

  useEffect(() => {
    localStorage.setItem("rf-formlist-hidden", showFormList ? "0" : "1");
  }, [showFormList]);

  // Initialise local state whenever a (different) form loads.
  useEffect(() => {
    if (!data) return;
    const initial: FormSchemaShape = data.draft_schema ??
      data.published_schema ?? {
        title: data.title,
        fields: [],
      };
    const seeded: FormSchemaShape = {
      ...initial,
      fields: initial.fields ?? [],
      sections: initial.sections ?? [],
    };
    setSchema(seeded);
    setTitle(data.title);
    setClientTitle(data.client_facing_title ?? "");
    setDescription(data.description ?? "");
    setCategory(data.category ?? "");
    setSelectedId(null);
    history.current = [seeded];
    historyPos.current = 0;
    forceHistoryRender((n) => n + 1);
  }, [data]);

  // Apply a schema mutation and push the result onto the undo stack.
  const commit = useCallback((next: FormSchemaShape) => {
    setSchema(next);
    const trimmed = history.current.slice(0, historyPos.current + 1);
    trimmed.push(next);
    history.current = trimmed;
    historyPos.current = trimmed.length - 1;
    forceHistoryRender((n) => n + 1);
  }, []);

  const canUndo = historyPos.current > 0;
  const canRedo = historyPos.current < history.current.length - 1;

  const undo = useCallback(() => {
    if (historyPos.current <= 0) return;
    historyPos.current -= 1;
    setSchema(history.current[historyPos.current]);
    forceHistoryRender((n) => n + 1);
  }, []);
  const redo = useCallback(() => {
    if (historyPos.current >= history.current.length - 1) return;
    historyPos.current += 1;
    setSchema(history.current[historyPos.current]);
    forceHistoryRender((n) => n + 1);
  }, []);

  const selectedField = useMemo(
    () => schema?.fields.find((f) => f.id === selectedId) ?? null,
    [schema, selectedId],
  );

  // ---- Field / section mutations -------------------------------------------

  const addField = useCallback(
    (type: FormFieldType, sectionId: string | null) => {
      if (!schema) return;
      const newField: FormFieldSchema = {
        id: cryptoRandomId(),
        type,
        label: defaultLabel(type),
        required: false,
        section_id: sectionId,
        ...(needsOptions(type)
          ? { options: [{ value: "opt1", label: "Option 1" }] }
          : {}),
      };
      commit({ ...schema, fields: [...schema.fields, newField] });
      setSelectedId(newField.id);
    },
    [schema, commit],
  );

  const updateField = useCallback(
    (fieldId: string, patch: Partial<FormFieldSchema>) => {
      if (!schema) return;
      commit({
        ...schema,
        fields: schema.fields.map((f) =>
          f.id === fieldId ? { ...f, ...patch } : f,
        ),
      });
    },
    [schema, commit],
  );

  const removeField = useCallback(
    (fieldId: string) => {
      if (!schema) return;
      commit({ ...schema, fields: schema.fields.filter((f) => f.id !== fieldId) });
      if (selectedId === fieldId) setSelectedId(null);
    },
    [schema, commit, selectedId],
  );

  // Duplicate a single field in place — handy when a form repeats near-identical
  // fields (e.g. one row per SharePoint site/library to grant access to). The
  // copy lands directly below the original, in the same section, with a fresh
  // id and a " (copy)" label. Nested structures are deep-copied so editing the
  // copy never mutates the original; conditional rules keep pointing at their
  // existing trigger fields (an empty id map copies without remapping).
  const duplicateField = useCallback(
    (fieldId: string) => {
      if (!schema) return;
      const idx = schema.fields.findIndex((f) => f.id === fieldId);
      if (idx === -1) return;
      const src = schema.fields[idx];
      const clone: FormFieldSchema = {
        ...src,
        id: cryptoRandomId(),
        label: `${src.label} (copy)`,
        options: src.options ? src.options.map((o) => ({ ...o })) : src.options,
        default_value: Array.isArray(src.default_value)
          ? [...src.default_value]
          : src.default_value,
        accepted_mime_types: src.accepted_mime_types
          ? [...src.accepted_mime_types]
          : src.accepted_mime_types,
        autotask_mapping: src.autotask_mapping
          ? { ...src.autotask_mapping }
          : src.autotask_mapping,
        show_when: remapConditionFieldIds(src.show_when, new Map()),
        required_when: remapConditionFieldIds(src.required_when, new Map()),
      };
      const fields = [...schema.fields];
      fields.splice(idx + 1, 0, clone);
      commit({ ...schema, fields });
      setSelectedId(clone.id);
    },
    [schema, commit],
  );

  const addSection = useCallback(() => {
    if (!schema) return;
    const newSection: FormSection = {
      id: cryptoRandomId(),
      title: `Section ${(schema.sections?.length ?? 0) + 1}`,
    };
    commit({ ...schema, sections: [...(schema.sections ?? []), newSection] });
  }, [schema, commit]);

  const updateSection = useCallback(
    (sectionId: string, patch: Partial<FormSection>) => {
      if (!schema) return;
      commit({
        ...schema,
        sections: (schema.sections ?? []).map((s) =>
          s.id === sectionId ? { ...s, ...patch } : s,
        ),
      });
    },
    [schema, commit],
  );

  // Deleting a section keeps its fields — they fall back to Ungrouped.
  const removeSection = useCallback(
    (sectionId: string) => {
      if (!schema) return;
      commit({
        ...schema,
        sections: (schema.sections ?? []).filter((s) => s.id !== sectionId),
        fields: schema.fields.map((f) =>
          f.section_id === sectionId ? { ...f, section_id: null } : f,
        ),
      });
    },
    [schema, commit],
  );

  // Copy every field (and its sections) from another form into this one. The
  // copy is appended after existing fields; all field/section ids are
  // regenerated so they never collide, and conditional rules (show_when /
  // required_when) are re-pointed at the new field ids so logic keeps working.
  const copyFieldsFrom = useCallback(
    (source: FormAdmin) => {
      if (!schema) return;
      const src = source.draft_schema ?? source.published_schema;
      if (!src || src.fields.length === 0) return;

      // Fresh ids for the source's sections, remembering old -> new.
      const sectionIdMap = new Map<string, string>();
      const clonedSections: FormSection[] = (src.sections ?? []).map((s) => {
        const newId = cryptoRandomId();
        sectionIdMap.set(s.id, newId);
        return { ...s, id: newId };
      });

      // Allocate new field ids up front so conditional rules can be remapped.
      const fieldIdMap = new Map<string, string>();
      for (const f of src.fields) fieldIdMap.set(f.id, cryptoRandomId());

      const clonedFields: FormFieldSchema[] = src.fields.map((f) => ({
        ...f,
        id: fieldIdMap.get(f.id)!,
        section_id: f.section_id
          ? (sectionIdMap.get(f.section_id) ?? null)
          : null,
        options: f.options ? f.options.map((o) => ({ ...o })) : f.options,
        default_value: Array.isArray(f.default_value)
          ? [...f.default_value]
          : f.default_value,
        accepted_mime_types: f.accepted_mime_types
          ? [...f.accepted_mime_types]
          : f.accepted_mime_types,
        autotask_mapping: f.autotask_mapping
          ? { ...f.autotask_mapping }
          : f.autotask_mapping,
        show_when: remapConditionFieldIds(f.show_when, fieldIdMap),
        required_when: remapConditionFieldIds(f.required_when, fieldIdMap),
      }));

      commit({
        ...schema,
        sections: [...(schema.sections ?? []), ...clonedSections],
        fields: [...schema.fields, ...clonedFields],
      });
      setStatusMsg(
        `Copied ${clonedFields.length} field${
          clonedFields.length === 1 ? "" : "s"
        } from "${source.title}". Review, then Save draft.`,
      );
    },
    [schema, commit],
  );

  // Move a field within or between sections after a SortableJS drag. The new
  // model is computed purely from the drag indices — we deliberately do NOT
  // read the Sortable-mutated DOM. The canvas reverts Sortable's DOM change in
  // onEnd, so React stays the single source of truth and never tries to
  // reconcile against a node Sortable already relocated (that mismatch
  // previously threw a removeChild error and greyed out the whole page).
  const moveField = useCallback(
    (fromSec: string, toSec: string, oldIndex: number, newIndex: number) => {
      if (!schema) return;
      const order = [...(schema.sections ?? []).map((s) => s.id), UNGROUPED];
      const buckets = new Map<string, FormFieldSchema[]>();
      for (const key of order) buckets.set(key, []);
      for (const f of schema.fields) {
        const key =
          f.section_id && buckets.has(f.section_id) ? f.section_id : UNGROUPED;
        buckets.get(key)!.push(f);
      }
      const fromArr = buckets.get(fromSec);
      const toArr = buckets.get(toSec);
      if (!fromArr || !toArr) return;
      if (oldIndex < 0 || oldIndex >= fromArr.length) return;
      const [moved] = fromArr.splice(oldIndex, 1);
      if (!moved) return;
      const newSectionId = toSec === UNGROUPED ? null : toSec;
      const updated =
        moved.section_id === newSectionId
          ? moved
          : { ...moved, section_id: newSectionId };
      toArr.splice(Math.max(0, Math.min(newIndex, toArr.length)), 0, updated);
      commit({
        ...schema,
        fields: order.flatMap((key) => buckets.get(key) ?? []),
      });
    },
    [schema, commit],
  );

  // Reorder sections from a drag, computed from indices (DOM reverted in
  // onEnd, same rationale as moveField).
  const moveSection = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (!schema) return;
      const secs = [...(schema.sections ?? [])];
      if (oldIndex < 0 || oldIndex >= secs.length) return;
      const [moved] = secs.splice(oldIndex, 1);
      if (!moved) return;
      secs.splice(Math.max(0, Math.min(newIndex, secs.length)), 0, moved);
      commit({ ...schema, sections: secs });
    },
    [schema, commit],
  );

  // ---- Save / publish (unchanged contract) ---------------------------------

  const saveDraft = useCallback(async (): Promise<boolean> => {
    if (!data || !schema) return false;
    setSaving(true);
    setError(null);
    setStatusMsg(null);
    try {
      const updatedSchema: FormSchemaShape = { ...schema, title: title.trim() };
      await api.admin.forms.updateDraft(data.id, {
        title: title.trim(),
        // Empty string clears the alias server-side (falls back to title).
        client_facing_title: clientTitle.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
        draft_schema: updatedSchema,
      });
      await mutate();
      setStatusMsg("Draft saved.");
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [data, schema, title, clientTitle, description, category, mutate]);

  const publish = useCallback(async () => {
    if (!data) return;
    if (
      !confirm(
        "Publish this form? It becomes visible to users immediately and replaces the previous published version. In-flight submissions are unaffected.",
      )
    ) {
      return;
    }
    const ok = await saveDraft();
    if (!ok) return;
    setPublishing(true);
    setError(null);
    try {
      await api.admin.forms.publish(data.id);
      await mutate();
      setStatusMsg("Form published.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Publish failed");
    } finally {
      setPublishing(false);
    }
  }, [data, saveDraft, mutate]);

  // Persist a client-assignment change immediately (form-level metadata, so it
  // applies to draft AND published forms). iQuest-admin only; the server
  // ignores scope changes from client_admins.
  const saveScope = useCallback(
    async (scope: FormScope, audienceIds: string[]) => {
      if (!data) return;
      setScopeSaving(true);
      setError(null);
      setStatusMsg(null);
      try {
        await api.admin.forms.updateDraft(data.id, {
          scope,
          company_id: null,
          audience_company_ids:
            scope === "multi_company" ? audienceIds : null,
        });
        await mutate();
        setStatusMsg("Assignment updated.");
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : "Failed to update assignment",
        );
      } finally {
        setScopeSaving(false);
      }
    },
    [data, mutate],
  );

  // Jump to a field from the Logic tab: select it and show the Fields tab.
  const editFieldFromLogic = useCallback((fieldId: string) => {
    setActiveTab("fields");
    setSelectedId(fieldId);
  }, []);

  if (loadErr) {
    return (
      <div className="card form-error">
        Failed to load form: {String(loadErr)}
      </div>
    );
  }
  if (!data || !schema) {
    return <div className="card">Loading form…</div>;
  }

  const fieldCount = schema.fields.length;
  const sectionCount = schema.sections?.length ?? 0;
  const ruleCount = schema.fields.filter(
    (f) => f.show_when || f.required_when,
  ).length;
  const clientFacing = clientTitle.trim() || title.trim() || "Untitled form";

  return (
    <div className="rf-wrap rf-wrap-fluid">
      <div className="rf-builder-head">
        <div>
          <div className="rf-builder-title-row">
            <h1 className="rf-builder-title">{title || "Untitled form"}</h1>
            <span className={`rf-status-pill ${data.is_published ? "published" : "draft"}`}>
              <span className="dot" />
              {data.is_published ? "Published" : "Draft"}
            </span>
          </div>
          <div className="rf-builder-clientline">
            <Icon name="eye" />
            <span className="tag">Client sees</span>
            <b>{clientFacing}</b>
          </div>
          <div className="rf-builder-meta">
            /{data.slug} · {fieldCount} field{fieldCount === 1 ? "" : "s"} ·{" "}
            {sectionCount} section{sectionCount === 1 ? "" : "s"}
          </div>
        </div>
        <div className="rf-builder-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setShowFormList((v) => !v)}
            title={
              showFormList
                ? "Hide the forms list for more room"
                : "Show the forms list"
            }
          >
            <Icon name="list" size={16} />
            {showFormList ? "Hide forms" : "Show forms"}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setShowPreview(true)}
            disabled={saving || publishing}
          >
            <Icon name="eye" size={16} />
            Preview
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void saveDraft()}
            disabled={saving || publishing}
          >
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void publish()}
            disabled={saving || publishing}
          >
            {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      <ClientBar
        scope={data.scope}
        companyId={data.company_id}
        audienceCompanyIds={data.audience_company_ids}
        companies={companies ?? []}
        canEdit={me?.role === "iquest_admin" || me?.role === "service_desk"}
        saving={scopeSaving}
        onSave={(scope, ids) => void saveScope(scope, ids)}
      />

      {statusMsg && <div className="form-status">{statusMsg}</div>}
      {error && <div className="form-error">{error}</div>}

      <div
        className={`rf-customiser${showFormList ? "" : " rf-customiser-nonav"}`}
      >
        {showFormList && (
          <FormListPanel
            forms={allForms ?? []}
            currentId={data.id}
            onOpen={(formId) => navigate(`/admin/forms/${formId}/edit`)}
            onNew={() => navigate("/admin/forms")}
          />
        )}

        <div className="rf-panel">
          <div className="rf-toolbar">
            <div className="left">
              <div className="rf-titlefield">
                <label className="rf-titlefield-lbl">
                  <span className="tag it">IT</span> Internal form name
                </label>
                <input
                  className="rf-title-input"
                  value={title}
                  maxLength={200}
                  placeholder="e.g. Thermal Installations New User Form"
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="rf-titlefield">
                <label className="rf-titlefield-lbl">
                  <span className="tag client">Client</span> What customers see
                </label>
                <input
                  className="rf-title-input client"
                  value={clientTitle}
                  maxLength={200}
                  placeholder="e.g. New user request"
                  onChange={(e) => setClientTitle(e.target.value)}
                />
              </div>
            </div>
            <div className="right">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                title="Copy all fields from another form into this one"
                onClick={() => setShowCopyFrom(true)}
              >
                <Icon name="copy" size={15} />
                Copy fields
              </button>
              <button
                type="button"
                className="rf-iconbtn"
                title="Undo"
                onClick={undo}
                disabled={!canUndo}
              >
                <Icon name="arrowLeft" size={16} />
              </button>
              <button
                type="button"
                className="rf-iconbtn"
                title="Redo"
                onClick={redo}
                disabled={!canRedo}
              >
                <Icon name="arrowRight" size={16} />
              </button>
            </div>
          </div>

          <div className="rf-tabs">
            {(
              [
                ["fields", "Fields", fieldCount],
                ["logic", "Logic", ruleCount],
                ["styling", "Styling", null],
                ["integrations", "Integrations", null],
              ] as Array<[BuilderTab, string, number | null]>
            ).map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                className={`rf-tab${activeTab === key ? " active" : ""}`}
                onClick={() => setActiveTab(key)}
                onFocus={(e) => e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" })}
              >
                {label}
                {count !== null && count > 0 && <span className="cnt">{count}</span>}
              </button>
            ))}
          </div>

          {activeTab === "fields" && (
            <FieldsCanvas
              schema={schema}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onRemoveField={removeField}
              onDuplicateField={duplicateField}
              onAddField={addField}
              onAddSection={addSection}
              onRenameSection={(sid, t) => updateSection(sid, { title: t })}
              onRemoveSection={removeSection}
              onSetWidth={(fid, w) => updateField(fid, { width: w })}
              onMoveField={moveField}
              onMoveSection={moveSection}
            />
          )}
          {activeTab === "logic" && (
            <LogicTab
              schema={schema}
              onChangeSchema={commit}
              onEditField={editFieldFromLogic}
            />
          )}
          {activeTab === "styling" && (
            <div className="rf-canvas">
              <StylingTab
                schema={schema}
                title={title}
                description={description}
                category={category}
                onTitle={setTitle}
                onDescription={setDescription}
                onCategory={setCategory}
                onSchema={commit}
              />
            </div>
          )}
          {activeTab === "integrations" && (
            <div className="rf-canvas">
              <IntegrationsTab
                schema={schema}
                autotaskFields={autotaskFields ?? FALLBACK_TARGETS}
                onSchema={commit}
              />
            </div>
          )}
        </div>

        <div className="rf-panel">
          <div className="rf-panel-head">
            <span className="t">Field properties</span>
          </div>
          <div className="rf-panel-body">
            <PropertiesPane
              field={selectedField}
              schema={schema}
              autotaskFields={autotaskFields ?? FALLBACK_TARGETS}
              autotaskDegraded={Boolean(autotaskDegraded)}
              onChange={(patch) =>
                selectedField && updateField(selectedField.id, patch)
              }
            />
          </div>
        </div>
      </div>

      {showPreview && (
        <PreviewModal
          schema={{ ...schema, title: clientFacing }}
          peopleCompanyId={
            data.scope === "single_company" ? data.company_id : null
          }
          onClose={() => setShowPreview(false)}
        />
      )}

      {showCopyFrom && (
        <CopyFieldsModal
          forms={allForms ?? []}
          currentId={data.id}
          onCopy={copyFieldsFrom}
          onClose={() => setShowCopyFrom(false)}
        />
      )}
    </div>
  );
}

// ---- Client assignment bar -------------------------------------------------

function ClientBar({
  scope,
  companyId,
  audienceCompanyIds,
  companies,
  canEdit,
  saving,
  onSave,
}: {
  scope: FormScope;
  companyId: string | null;
  audienceCompanyIds: string[];
  companies: AdminCompanyRow[];
  canEdit: boolean;
  saving: boolean;
  onSave: (scope: FormScope, audienceIds: string[]) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  // Current selection derived from the form's scope (server is source of truth).
  const selected =
    scope === "global"
      ? []
      : scope === "single_company" && companyId
        ? [companyId]
        : audienceCompanyIds;
  const isAll = selected.length === 0;
  const nameOf = (id: string) =>
    companies.find((c) => c.id === id)?.name ?? "Unknown client";
  const available = companies.filter((c) => !selected.includes(c.id));

  function addClient(id: string) {
    onSave("multi_company", [...selected, id]);
    setMenuOpen(false);
  }
  function removeClient(id: string) {
    const next = selected.filter((x) => x !== id);
    if (next.length === 0) onSave("global", []);
    else onSave("multi_company", next);
  }

  return (
    <div className="rf-clientbar">
      <span className="rf-clientbar-label">
        <Icon name="admin" />
        Assigned to
      </span>
      <div className="chips">
        {isAll ? (
          <span className="client-chip all-clients">All clients</span>
        ) : (
          selected.map((id) => (
            <span className="client-chip" key={id}>
              {nameOf(id)}
              {canEdit && (
                <span
                  className="remove"
                  style={{ cursor: "pointer", marginLeft: 4 }}
                  title="Remove client"
                  onClick={() => removeClient(id)}
                >
                  ×
                </span>
              )}
            </span>
          ))
        )}
        {canEdit && (
          <div ref={wrapRef} style={{ position: "relative", display: "inline-flex" }}>
            <button
              type="button"
              className="rf-add-client"
              disabled={saving}
              title="Assign a client"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Icon name="plus" size={14} />
            </button>
            {menuOpen && (
              <div className="field-type-menu" style={{ width: 220 }}>
                {!isAll && (
                  <button
                    type="button"
                    className="field-type-menu-item"
                    onClick={() => {
                      onSave("global", []);
                      setMenuOpen(false);
                    }}
                  >
                    <span className="field-type-menu-label">All clients</span>
                  </button>
                )}
                {available.length === 0 ? (
                  <div
                    className="field-type-menu-blurb"
                    style={{ padding: "8px 10px" }}
                  >
                    No more clients to add.
                  </div>
                ) : (
                  available.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="field-type-menu-item"
                      onClick={() => addClient(c.id)}
                    >
                      <span className="field-type-menu-label">{c.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <span style={{ fontSize: 11, color: "var(--iq-text-faint)", whiteSpace: "nowrap" }}>
        {!canEdit
          ? "Set by iQuest"
          : saving
            ? "Saving…"
            : "Applies to draft + published"}
      </span>
    </div>
  );
}

// ---- Left panel: form list (grouped by client-facing name) -----------------

function formListIcon(f: FormAdmin): IconName {
  const hay = `${f.category ?? ""} ${f.client_facing_title ?? ""} ${f.title} ${f.slug}`.toLowerCase();
  if (/offboard|leaver|depart/.test(hay)) return "userMinus";
  if (/onboard|new starter|new-starter|joiner|new user/.test(hay)) return "userPlus";
  if (/access|permission|account|licen[cs]e/.test(hay)) return "key";
  if (/hardware|device|laptop|equipment/.test(hay)) return "monitor";
  if (/security|mfa|phish/.test(hay)) return "shield";
  if (/mail|email|software/.test(hay)) return "mail";
  return "doc";
}

function FormListPanel({
  forms,
  currentId,
  onOpen,
  onNew,
}: {
  forms: FormAdmin[];
  currentId: string;
  onOpen: (id: string) => void;
  onNew: () => void;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? forms.filter(
        (f) =>
          f.title.toLowerCase().includes(q) ||
          (f.client_facing_title ?? "").toLowerCase().includes(q) ||
          f.slug.toLowerCase().includes(q) ||
          (f.category ?? "").toLowerCase().includes(q),
      )
    : forms;

  // Forms with a client-facing alias group under that name (dual naming, §6.3);
  // the rest fall under "Other forms". Within an aliased group, drafts and
  // published are sub-headed so variants read distinctly.
  const aliasGroups = new Map<string, FormAdmin[]>();
  const other: FormAdmin[] = [];
  for (const f of filtered) {
    const alias = (f.client_facing_title ?? "").trim();
    if (alias) {
      const bucket = aliasGroups.get(alias) ?? [];
      bucket.push(f);
      aliasGroups.set(alias, bucket);
    } else {
      other.push(f);
    }
  }
  const aliasNames = Array.from(aliasGroups.keys()).sort((a, b) => a.localeCompare(b));

  const renderItem = (f: FormAdmin, showClientLine: boolean) => {
    const shape = f.draft_schema ?? f.published_schema ?? null;
    const color: FormIconColor = shape?.icon_color ?? "violet";
    const fieldCount = shape?.fields?.length ?? 0;
    const sectionCount = shape?.sections?.length ?? 0;
    return (
      <a
        key={f.id}
        className={`rf-formitem${f.id === currentId ? " active" : ""}`}
        href={`/admin/forms/${f.id}/edit`}
        onClick={(e) => {
          e.preventDefault();
          onOpen(f.id);
        }}
      >
        <span className={`rf-formitem-icon ${color}`}>
          <Icon name={formListIcon(f)} size={17} />
        </span>
        <span className="rf-formitem-text">
          <span className="rf-formitem-name">{f.title}</span>
          {showClientLine && f.client_facing_title ? (
            <span className="rf-formitem-client">
              <Icon name="eye" size={12} />
              {f.client_facing_title}
            </span>
          ) : (
            <span className="rf-formitem-meta">
              {fieldCount} field{fieldCount === 1 ? "" : "s"} · {sectionCount}{" "}
              section{sectionCount === 1 ? "" : "s"}
            </span>
          )}
        </span>
        <span className={`rf-scope-badge${f.scope === "global" ? " all" : ""}`}>
          {scopeBadge(f)}
        </span>
      </a>
    );
  };

  return (
    <div className="rf-panel">
      <div className="rf-panel-head">
        <span className="t">Forms</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onNew}>
          <Icon name="plus" size={14} /> New
        </button>
      </div>
      <div className="rf-panel-body">
        <div className="rf-list-search">
          <Icon name="search" />
          <input
            type="text"
            placeholder="Search by IT or client-facing name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {aliasNames.map((alias) => {
          const group = aliasGroups.get(alias) ?? [];
          const drafts = group.filter((f) => !f.is_published);
          const published = group.filter((f) => f.is_published);
          return (
            <div key={alias}>
              <div className="rf-list-grouptag">
                <span className="tx">
                  <Icon name="eye" size={13} /> Client-facing: <b>{alias}</b>
                </span>
                <span className="ln" />
              </div>
              {drafts.length > 0 && (
                <>
                  <div className="rf-list-subhead">
                    <span className="lbl">Drafts</span>
                    <span className="ct">{drafts.length}</span>
                  </div>
                  {drafts.map((f) => renderItem(f, true))}
                </>
              )}
              {published.length > 0 && (
                <>
                  <div className="rf-list-subhead">
                    <span className="lbl">Published</span>
                    <span className="ct">{published.length}</span>
                  </div>
                  {published.map((f) => renderItem(f, true))}
                </>
              )}
            </div>
          );
        })}

        {other.length > 0 && (
          <>
            {aliasNames.length > 0 && (
              <div className="rf-list-grouptag">
                <span className="tx">Other forms</span>
                <span className="ln" />
              </div>
            )}
            {other.map((f) => renderItem(f, false))}
          </>
        )}

        {filtered.length === 0 && (
          <p className="empty-state-blurb" style={{ padding: "12px 4px" }}>
            No forms match.
          </p>
        )}
      </div>
    </div>
  );
}

// ---- Center: Fields tab (sections + sortable field rows) -------------------

interface FieldsCanvasProps {
  schema: FormSchemaShape;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRemoveField: (fieldId: string) => void;
  onDuplicateField: (fieldId: string) => void;
  onAddField: (type: FormFieldType, sectionId: string | null) => void;
  onAddSection: () => void;
  onRenameSection: (sectionId: string, title: string) => void;
  onRemoveSection: (sectionId: string) => void;
  onSetWidth: (fieldId: string, width: "half" | "full") => void;
  onMoveField: (
    fromSection: string,
    toSection: string,
    oldIndex: number,
    newIndex: number,
  ) => void;
  onMoveSection: (oldIndex: number, newIndex: number) => void;
}

function FieldsCanvas({
  schema,
  selectedId,
  onSelect,
  onRemoveField,
  onDuplicateField,
  onAddField,
  onAddSection,
  onRenameSection,
  onRemoveSection,
  onSetWidth,
  onMoveField,
  onMoveSection,
}: FieldsCanvasProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const sectionsRef = useRef<HTMLDivElement | null>(null);
  // Records where a dragged element started so onEnd can undo Sortable's DOM
  // mutation before React re-renders (prevents the removeChild crash).
  const dragOrigin = useRef<{ parent: Node; next: Node | null } | null>(null);

  const sections = schema.sections ?? [];
  const fieldsBySection = useMemo(() => {
    const map = new Map<string, FormFieldSchema[]>();
    map.set(UNGROUPED, []);
    for (const s of sections) map.set(s.id, []);
    for (const f of schema.fields) {
      const key = f.section_id && map.has(f.section_id) ? f.section_id : UNGROUPED;
      map.get(key)!.push(f);
    }
    return map;
  }, [schema.fields, sections]);

  const ungrouped = fieldsBySection.get(UNGROUPED) ?? [];

  // Bind one shared Sortable per section list, all in the same drag group so
  // rows can move between sections. Rebind when the structure changes. The
  // drag handle is the row grip; the `.rf-fgrid` carries the [data-section].
  const structureKey =
    sections.map((s) => s.id).join(",") + "|" + schema.fields.length;
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const instances: Sortable[] = [];
    root.querySelectorAll<HTMLElement>("[data-section]").forEach((listEl) => {
      instances.push(
        Sortable.create(listEl, {
          group: "form-fields",
          animation: 120,
          handle: ".rf-frow-grip",
          ghostClass: "dragging",
          // Pointer-driven drag (not native HTML5 DnD) so SortableJS's
          // AutoScroll plugin can scroll .rf-canvas while a row is held —
          // native drag does not autoscroll nested overflow containers in
          // Chrome (issue #12). Larger sensitivity = friendlier scroll zone.
          forceFallback: true,
          fallbackOnBody: true,
          scroll: true,
          scrollSensitivity: 80,
          scrollSpeed: 12,
          onStart: (evt) => {
            dragOrigin.current = {
              parent: evt.item.parentNode as Node,
              next: evt.item.nextSibling,
            };
          },
          onEnd: (evt) => {
            // Undo Sortable's DOM move so React's vdom matches the real DOM,
            // then drive the change through state instead.
            const origin = dragOrigin.current;
            dragOrigin.current = null;
            if (origin) origin.parent.insertBefore(evt.item, origin.next);
            const fromSec = evt.from.dataset.section ?? UNGROUPED;
            const toSec = evt.to.dataset.section ?? UNGROUPED;
            const { oldIndex, newIndex } = evt;
            if (oldIndex === undefined || newIndex === undefined) return;
            if (fromSec === toSec && oldIndex === newIndex) return;
            onMoveField(fromSec, toSec, oldIndex, newIndex);
          },
        }),
      );
    });
    return () => instances.forEach((s) => s.destroy());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureKey, onMoveField]);

  // Sections themselves are reorderable via their header grip.
  useEffect(() => {
    const el = sectionsRef.current;
    if (!el) return;
    const s = Sortable.create(el, {
      animation: 120,
      handle: ".rf-bsection-grip",
      draggable: ".rf-bsection",
      // Fallback mode enables autoscroll while dragging — see the field list
      // above (issue #12).
      forceFallback: true,
      fallbackOnBody: true,
      scroll: true,
      scrollSensitivity: 80,
      scrollSpeed: 12,
      onStart: (evt) => {
        dragOrigin.current = {
          parent: evt.item.parentNode as Node,
          next: evt.item.nextSibling,
        };
      },
      onEnd: (evt) => {
        const origin = dragOrigin.current;
        dragOrigin.current = null;
        if (origin) origin.parent.insertBefore(evt.item, origin.next);
        const { oldIndex, newIndex } = evt;
        if (oldIndex === undefined || newIndex === undefined) return;
        if (oldIndex === newIndex) return;
        onMoveSection(oldIndex, newIndex);
      },
    });
    return () => s.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.map((s) => s.id).join(","), onMoveSection]);

  return (
    <div className="rf-canvas" ref={rootRef}>
      <div ref={sectionsRef}>
        {sections.map((section) => (
          <div className="rf-bsection" key={section.id} data-sid={section.id}>
            <div className="rf-bsection-head">
              <div className="left">
                <span className="grip rf-bsection-grip" aria-hidden title="Drag to reorder section">
                  {gripIcon()}
                </span>
                <input
                  value={section.title}
                  placeholder="Section title"
                  onChange={(e) => onRenameSection(section.id, e.target.value)}
                />
              </div>
              <div className="acts">
                <button
                  type="button"
                  title="Delete section (keeps its fields)"
                  onClick={() => onRemoveSection(section.id)}
                >
                  {trashIcon()}
                </button>
              </div>
            </div>
            <SectionFieldList
              sectionKey={section.id}
              fields={fieldsBySection.get(section.id) ?? []}
              selectedId={selectedId}
              onSelect={onSelect}
              onRemoveField={onRemoveField}
              onDuplicateField={onDuplicateField}
              onSetWidth={onSetWidth}
            />
            <AddFieldButton
              onPick={(type) => onAddField(type, section.id)}
              label="Add field"
            />
          </div>
        ))}
      </div>

      {/* Ungrouped bucket — always present so there's a drop target. */}
      <div className="rf-bsection">
        {sections.length > 0 && (
          <div className="rf-bsection-head">
            <div className="left">
              <span style={{ color: "var(--iq-text-faint)", fontWeight: 600, fontSize: 13 }}>
                Ungrouped
              </span>
            </div>
          </div>
        )}
        <SectionFieldList
          sectionKey={UNGROUPED}
          fields={ungrouped}
          selectedId={selectedId}
          onSelect={onSelect}
          onRemoveField={onRemoveField}
          onDuplicateField={onDuplicateField}
          onSetWidth={onSetWidth}
        />
        <AddFieldButton onPick={(type) => onAddField(type, null)} label="Add field" />
      </div>

      {schema.fields.length === 0 && sections.length === 0 && (
        <div className="rf-prop-empty">
          <div className="ic">
            <Icon name="list" size={20} />
          </div>
          <div className="t">Empty form</div>
          <div className="s">Add a field or a section to get started.</div>
        </div>
      )}

      <button type="button" className="rf-add-section" onClick={onAddSection}>
        <Icon name="plus" size={16} /> Add section
      </button>
    </div>
  );
}

function SectionFieldList({
  sectionKey,
  fields,
  selectedId,
  onSelect,
  onRemoveField,
  onDuplicateField,
  onSetWidth,
}: {
  sectionKey: string;
  fields: FormFieldSchema[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRemoveField: (fieldId: string) => void;
  onDuplicateField: (fieldId: string) => void;
  onSetWidth: (fieldId: string, width: "half" | "full") => void;
}) {
  // The grid IS the Sortable list (rows are its direct children), so half-width
  // fields pair up two-up while still being draggable.
  return (
    <div className="rf-fgrid" data-section={sectionKey} style={{ minHeight: 8 }}>
      {fields.map((field) => (
        <FieldRow
          key={field.id}
          field={field}
          selected={field.id === selectedId}
          onSelect={() => onSelect(field.id)}
          onRemove={() => onRemoveField(field.id)}
          onDuplicate={() => onDuplicateField(field.id)}
          onSetWidth={(w) => onSetWidth(field.id, w)}
        />
      ))}
    </div>
  );
}

function FieldRow({
  field,
  selected,
  onSelect,
  onRemove,
  onDuplicate,
  onSetWidth,
}: {
  field: FormFieldSchema;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSetWidth: (width: "half" | "full") => void;
}) {
  const width = field.width ?? "full";
  const mapTarget = field.autotask_mapping
    ? field.autotask_mapping.target === "udf"
      ? field.autotask_mapping.udf_name || "udf"
      : field.autotask_mapping.target
    : null;

  return (
    <div
      data-fid={field.id}
      data-w={width}
      className={`rf-frow${selected ? " selected" : ""}`}
      onClick={onSelect}
    >
      <span className="grip rf-frow-grip" aria-hidden title="Drag to reorder">
        {gripIcon()}
      </span>
      <span className="rf-frow-icon">{iconForType(field.type)}</span>
      <div className="rf-frow-info">
        <span className="rf-frow-name">{field.label}</span>
        <span className="rf-frow-type">{TYPE_LABEL[field.type] ?? field.type}</span>
      </div>
      <div className="rf-frow-badges">
        {field.required && <span className="rf-fbadge req">REQ</span>}
        {mapTarget && <span className="rf-fbadge map">→ {mapTarget}</span>}
        {field.show_when && <span className="rf-fbadge cond">IF</span>}
      </div>
      {field.type !== "section_header" && (
        <span className="rf-wtoggle" title="Field width" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            data-w="half"
            className={width === "half" ? "on" : ""}
            title="Half width"
            onClick={() => onSetWidth("half")}
          >
            {widthIcon("half")}
          </button>
          <button
            type="button"
            data-w="full"
            className={width === "full" ? "on" : ""}
            title="Full width"
            onClick={() => onSetWidth("full")}
          >
            {widthIcon("full")}
          </button>
        </span>
      )}
      <button
        type="button"
        className="rf-frow-dup"
        title="Duplicate field"
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate();
        }}
      >
        <Icon name="copy" size={15} />
      </button>
      <button
        type="button"
        className="rf-frow-del"
        title="Delete field"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        {trashIcon()}
      </button>
    </div>
  );
}

// ---- Add-field button with field-type popover ------------------------------

function AddFieldButton({
  onPick,
  label,
}: {
  onPick: (type: FormFieldType) => void;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button type="button" className="rf-addfield" onClick={() => setOpen((o) => !o)}>
        <Icon name="plus" size={15} /> {label}
      </button>
      {open && (
        <div className="field-type-menu">
          {FIELD_TYPES.map((t) => (
            <button
              key={t.type}
              type="button"
              className="field-type-menu-item"
              onClick={() => {
                onPick(t.type);
                setOpen(false);
              }}
            >
              <span className="field-icon">{iconForType(t.type)}</span>
              <span>
                <span className="field-type-menu-label">{t.label}</span>
                <br />
                <span className="field-type-menu-blurb">{t.blurb}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Center: Logic tab (rules overview) ------------------------------------

function LogicTab({
  schema,
  onChangeSchema,
  onEditField,
}: {
  schema: FormSchemaShape;
  onChangeSchema: (next: FormSchemaShape) => void;
  onEditField: (fieldId: string) => void;
}) {
  const dataFields = schema.fields.filter((f) => f.type !== "section_header");
  const numberFields = dataFields.filter((f) => f.type === "number");

  // One statement card per visibility / requirement rule.
  type RuleEntry = {
    field: FormFieldSchema;
    lead: "Show" | "Require";
    expr: ConditionalExpression;
  };
  const rules: RuleEntry[] = [];
  for (const f of dataFields) {
    if (f.show_when) rules.push({ field: f, lead: "Show", expr: f.show_when });
    if (f.required_when) rules.push({ field: f, lead: "Require", expr: f.required_when });
  }

  return (
    <div className="rf-logic-canvas">
      <div className="rf-logic-intro">
        <div className="t">Conditional logic</div>
        <div className="s">
          Rules decide which fields appear (and when they&apos;re required) while
          someone fills in the form. They run live — fields hidden by a rule
          aren&apos;t required and aren&apos;t submitted. Turn on
          &ldquo;Conditional display&rdquo; on any field to add one.
        </div>
      </div>

      {numberFields.length > 0 && (
        <div className="rf-prop-field" style={{ maxWidth: 420, marginBottom: 22 }}>
          <label className="rf-prop-label">Approval value field</label>
          <select
            className="rf-prop-select"
            value={schema.approval_value_field_id ?? ""}
            onChange={(e) =>
              onChangeSchema({
                ...schema,
                approval_value_field_id: e.target.value || null,
              })
            }
          >
            <option value="">— none —</option>
            {numberFields.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
          <p className="rf-prop-hint">
            The numeric field whose value drives the approval matrix.
          </p>
        </div>
      )}

      {rules.length === 0 ? (
        <div className="rf-prop-empty">
          <div className="ic">
            <Icon name="link" size={20} />
          </div>
          <div className="t">No rules yet</div>
          <div className="s">
            Select a field, then turn on &ldquo;Conditional display&rdquo; in its
            properties to create one.
          </div>
        </div>
      ) : (
        rules.map((r, i) => {
          const model = exprToModel(r.expr);
          const conds = model?.rules ?? [];
          const matchWord = model?.match === "any" ? "or" : "and";
          return (
            <div className="rf-logic-rule" key={`${r.field.id}-${r.lead}-${i}`}>
              <div className="rf-logic-badge">IF</div>
              <div className="rf-logic-body">
                <div className="rf-logic-statement">
                  {r.lead} <span className="show">{r.field.label}</span> only when{" "}
                  {model ? (
                    <>
                      {conds.map((c, ci) => {
                        const trig = schema.fields.find((f) => f.id === c.fieldId);
                        const label =
                          trig?.type === "single_checkbox"
                            ? "ticked"
                            : trig?.options?.find((o) => o.value === c.value)?.label ?? c.value;
                        return (
                          <span key={ci}>
                            {ci > 0 && <span className="op"> {matchWord} </span>}
                            <span className="fld">{trig?.label ?? c.fieldId}</span>{" "}
                            <span className="op">{OP_LABEL[c.op]}</span>{" "}
                            <span className="val">{label}</span>
                          </span>
                        );
                      })}
                      .
                    </>
                  ) : (
                    <span className="op">a custom rule is met.</span>
                  )}
                </div>
                <div className="rf-logic-meta">
                  <span className="dot" />{" "}
                  {model
                    ? `Active · ${conds.length} condition${conds.length === 1 ? "" : "s"} · match ${model.match.toUpperCase()}`
                    : "Active · advanced rule"}
                </div>
              </div>
              <div className="rf-logic-actions">
                <button
                  type="button"
                  className="rf-logic-edit"
                  onClick={() => onEditField(r.field.id)}
                >
                  Edit
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

// ---- Center: Styling tab ---------------------------------------------------

function StylingTab({
  schema,
  title,
  description,
  category,
  onTitle,
  onDescription,
  onCategory,
  onSchema,
}: {
  schema: FormSchemaShape;
  title: string;
  description: string;
  category: string;
  onTitle: (v: string) => void;
  onDescription: (v: string) => void;
  onCategory: (v: string) => void;
  onSchema: (next: FormSchemaShape) => void;
}) {
  return (
    <>
      <div className="rf-prop-grouptitle">Presentation</div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Internal title</label>
        <input
          className="rf-prop-input"
          value={title}
          maxLength={200}
          onChange={(e) => onTitle(e.target.value)}
        />
        <p className="rf-prop-hint">
          The IT-facing name. Edit the customer-facing name in the toolbar above.
        </p>
      </div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Description</label>
        <textarea
          className="rf-prop-textarea"
          rows={3}
          value={description}
          onChange={(e) => onDescription(e.target.value)}
        />
      </div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Submit button label</label>
        <input
          className="rf-prop-input"
          value={schema.submit_label ?? ""}
          placeholder="Submit"
          onChange={(e) => onSchema({ ...schema, submit_label: e.target.value || undefined })}
        />
      </div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Category</label>
        <input
          className="rf-prop-input"
          value={category}
          maxLength={64}
          onChange={(e) => onCategory(e.target.value)}
        />
      </div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Icon colour</label>
        <div style={{ display: "flex", gap: 8 }}>
          {ICON_COLORS.map((c) => {
            const active = (schema.icon_color ?? "violet") === c;
            return (
              <button
                key={c}
                type="button"
                className={`rf-card-icon ${c}`}
                title={c}
                style={{
                  width: 34,
                  height: 34,
                  cursor: "pointer",
                  border: active ? "2px solid var(--iq-violet)" : "2px solid transparent",
                }}
                onClick={() => onSchema({ ...schema, icon_color: c })}
              >
                <Icon name="doc" size={16} />
              </button>
            );
          })}
        </div>
      </div>

      <div className="rf-prop-grouptitle">Service Hub rail</div>
      <p className="rf-prop-hint" style={{ margin: "0 0 12px" }}>
        Shown on the catalogue card and the form&apos;s guidance rail. All optional.
      </p>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Estimated time</label>
        <input
          className="rf-prop-input"
          value={schema.est_time ?? ""}
          placeholder="e.g. 5-7 min"
          onChange={(e) => onSchema({ ...schema, est_time: e.target.value || undefined })}
        />
      </div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">Target SLA</label>
        <input
          className="rf-prop-input"
          value={schema.sla_target ?? ""}
          placeholder="e.g. 2 business days"
          onChange={(e) => onSchema({ ...schema, sla_target: e.target.value || undefined })}
        />
      </div>

      <div className="rf-prop-field">
        <label className="rf-prop-label">This produces (one per line)</label>
        <textarea
          className="rf-prop-textarea"
          rows={4}
          value={(schema.outputs ?? []).join("\n")}
          placeholder={"Account creation\nMicrosoft 365 licence"}
          onChange={(e) =>
            onSchema({
              ...schema,
              outputs: e.target.value
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </div>
    </>
  );
}

// ---- Center: Integrations tab ----------------------------------------------

// Light client-side email check for the internal-notification recipient rows.
// The server re-validates on save/publish (form_schema._EMAIL_RE) and the
// recipient-domain allow-list is the real send-time guard.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// Matches the backend cap (FormInternalNotifyConfig.recipients max_length).
const MAX_INTERNAL_RECIPIENTS = 25;

function IntegrationsTab({
  schema,
  autotaskFields,
  onSchema,
}: {
  schema: FormSchemaShape;
  autotaskFields: AutotaskFieldTarget[];
  onSchema: (next: FormSchemaShape) => void;
}) {
  const mapped = schema.fields.filter((f) => f.autotask_mapping);

  return (
    <>
      <div className="rf-prop-grouptitle">Note on the ticket</div>
      <div className="rf-prop-field">
        <label className="rf-prop-label">Note added to the ticket description</label>
        <textarea
          className="rf-prop-textarea"
          rows={5}
          value={schema.ticket_description_note ?? ""}
          placeholder={
            "Context for the technician (routing, gotchas, useful links).\n" +
            "This is not asked on the form, but it does show on the ticket."
          }
          onChange={(e) =>
            onSchema({ ...schema, ticket_description_note: e.target.value || null })
          }
        />
        <p className="rf-prop-hint">
          Appended to the description of every ticket this form creates. The
          customer can see it on the ticket. Leave empty for none.
        </p>
      </div>

      <div className="rf-prop-grouptitle">Internal notifications</div>
      <p className="rf-prop-hint" style={{ margin: "0 0 12px" }}>
        Email internal people the full submission when a request from this form
        is <strong>fully approved</strong>. Use it to loop in departments beyond
        IT (finance, warehouse, projects). Only fires for forms that route
        through an approval workflow; recipients must be on an approved email
        domain (e.g. iquest.com.au).
      </p>
      {(() => {
        const notif: FormInternalNotifyConfig =
          schema.internal_notifications ?? { enabled: false, recipients: [] };
        const setNotif = (next: FormInternalNotifyConfig) => {
          // Drop the whole block when it is off and empty, to keep the schema tidy.
          const cleaned =
            !next.enabled && next.recipients.length === 0 ? null : next;
          onSchema({ ...schema, internal_notifications: cleaned });
        };
        const updateRecipient = (
          i: number,
          patch: Partial<FormInternalNotifyRecipient>,
        ) =>
          setNotif({
            ...notif,
            recipients: notif.recipients.map((r, idx) =>
              idx === i ? { ...r, ...patch } : r,
            ),
          });
        const removeRecipient = (i: number) =>
          setNotif({
            ...notif,
            recipients: notif.recipients.filter((_, idx) => idx !== i),
          });
        const atLimit = notif.recipients.length >= MAX_INTERNAL_RECIPIENTS;
        const addRecipient = () => {
          if (atLimit) return;
          setNotif({
            ...notif,
            enabled: true,
            recipients: [...notif.recipients, { email: "", name: "" }],
          });
        };

        return (
          <>
            <div
              className={`rf-prop-toggle${notif.enabled ? " on" : ""}`}
              onClick={() => setNotif({ ...notif, enabled: !notif.enabled })}
            >
              <div>
                <div className="lbl">Notify on final approval</div>
                <div className="sub">
                  Emails the recipients below the full submission
                </div>
              </div>
              <span className="rf-mini-toggle" />
            </div>

            {notif.recipients.length === 0 ? (
              <div className="rf-prop-empty" style={{ marginTop: 10 }}>
                <div className="ic">
                  <Icon name="mail" size={20} />
                </div>
                <div className="t">No recipients</div>
                <div className="s">
                  Add the internal people who should be notified.
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  marginTop: 10,
                }}
              >
                {notif.recipients.map((r, i) => {
                  const email = (r.email ?? "").trim();
                  const invalid = email.length > 0 && !EMAIL_RE.test(email);
                  return (
                    <div
                      key={i}
                      style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
                    >
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        <input
                          className="rf-prop-input"
                          value={r.name ?? ""}
                          placeholder="Name (optional), e.g. Finance Team"
                          onChange={(e) =>
                            updateRecipient(i, { name: e.target.value || null })
                          }
                        />
                        <input
                          className="rf-prop-input"
                          value={r.email ?? ""}
                          placeholder="name@iquest.com.au"
                          aria-invalid={invalid}
                          style={
                            invalid
                              ? { borderColor: "var(--iq-red, #d64545)" }
                              : undefined
                          }
                          onChange={(e) =>
                            updateRecipient(i, { email: e.target.value })
                          }
                        />
                        {invalid && (
                          <p
                            className="rf-prop-hint"
                            style={{ color: "var(--iq-red, #d64545)", margin: 0 }}
                          >
                            That does not look like a valid email address.
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        className="rf-iconbtn"
                        title="Remove recipient"
                        aria-label="Remove recipient"
                        onClick={() => removeRecipient(i)}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              className="rf-add-opt"
              style={{ marginTop: 10 }}
              onClick={addRecipient}
              disabled={atLimit}
            >
              <Icon name="plus" size={13} /> Add recipient
            </button>
            {atLimit && (
              <p className="rf-prop-hint" style={{ margin: "6px 0 0" }}>
                Limit of {MAX_INTERNAL_RECIPIENTS} recipients reached.
              </p>
            )}
          </>
        );
      })()}

      <div className="rf-prop-grouptitle">Autotask ticket type</div>
      <div className="rf-prop-field">
        <label className="rf-prop-label">Approval form type override</label>
        <input
          className="rf-prop-input"
          value={schema.approval_form_type_slug ?? ""}
          placeholder="(leave empty to use the form slug)"
          onChange={(e) =>
            onSchema({ ...schema, approval_form_type_slug: e.target.value || null })
          }
        />
        <p className="rf-prop-hint">
          Empty = use this form&apos;s slug as the approval form type.
        </p>
      </div>

      <div className="rf-prop-grouptitle">Field mappings</div>
      {mapped.length === 0 ? (
        <div className="rf-prop-empty">
          <div className="ic">
            <Icon name="link" size={20} />
          </div>
          <div className="t">No fields mapped</div>
          <div className="s">
            Select a field, then map it to an Autotask target in the Properties pane.
          </div>
        </div>
      ) : (
        <div className="rf-mapping">
          {mapped.map((f) => {
            const m = f.autotask_mapping!;
            const targetLabel =
              m.target === "udf"
                ? `udf:${m.udf_name ?? ""}`
                : autotaskFields.find((t) => t.target === m.target)?.target ?? m.target;
            return (
              <div className="rf-mapping-row" key={f.id} style={{ marginBottom: 6 }}>
                <span className="k">{f.label}</span>
                <span className="v">{targetLabel}</span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---- Right: properties pane ------------------------------------------------

interface PropertiesPaneProps {
  field: FormFieldSchema | null;
  schema: FormSchemaShape;
  autotaskFields: AutotaskFieldTarget[];
  autotaskDegraded: boolean;
  onChange: (patch: Partial<FormFieldSchema>) => void;
}

function PropertiesPane({
  field,
  schema,
  autotaskFields,
  autotaskDegraded,
  onChange,
}: PropertiesPaneProps) {
  if (!field) {
    return (
      <div className="rf-prop-empty">
        <div className="ic">
          <Icon name="settings" size={20} />
        </div>
        <div className="t">No field selected</div>
        <div className="s">Click a field in the canvas to edit its properties.</div>
      </div>
    );
  }

  const isSection = field.type === "section_header";
  const showOptions = needsOptions(field.type);
  const sections = schema.sections ?? [];
  const mapping = field.autotask_mapping ?? null;
  const selectedTarget = autotaskFields.find((t) => t.target === mapping?.target);

  // Eligible triggers: any other choice field on the form with a discrete
  // value set (a field can't reference itself). Earlier and later fields both
  // qualify — a rule reads the referenced answer at fill time regardless of
  // where that field sits in the form.
  const triggers: Trigger[] = schema.fields
    .filter((f) => f.id !== field.id && TRIGGER_TYPES.includes(f.type))
    .map((f) => ({
      id: f.id,
      label: f.label,
      multi: MULTI_TRIGGER_TYPES.includes(f.type),
      bool: f.type === "single_checkbox",
      options: f.type === "single_checkbox" ? CHECKBOX_TRIGGER_OPTIONS : f.options ?? [],
    }));

  return (
    <div>
      {/* Hero: label first and largest. */}
      <div className="rf-prop-hero">
        <div className="ribbon">
          <span className="ic">{iconForType(field.type)}</span>
          <span className="ty">
            {TYPE_LABEL[field.type] ?? field.type} · {field.label}
          </span>
        </div>
        <label className="rf-prop-label hero">Field label</label>
        <input
          className="rf-prop-input hero"
          value={field.label}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      {/* Options — kept directly under the label so the two most-used parts sit
          together rather than scrolling past General/Layout (issue #20). */}
      {showOptions && (
        <>
          <div className="rf-prop-grouptitle">Options</div>
          <OptionsEditor
            options={field.options ?? []}
            fieldType={field.type}
            defaultValue={field.default_value ?? null}
            onChange={(opts, dv) =>
              onChange({ options: opts, default_value: dv })
            }
          />
        </>
      )}

      {/* General */}
      {!isSection && (
        <>
          <div className="rf-prop-grouptitle">General</div>
          <div className="rf-prop-field">
            <label className="rf-prop-label">Placeholder</label>
            <input
              className="rf-prop-input"
              value={field.placeholder ?? ""}
              onChange={(e) => onChange({ placeholder: e.target.value || null })}
            />
          </div>
          <div className="rf-prop-field">
            <label className="rf-prop-label">Help text</label>
            <textarea
              className="rf-prop-textarea"
              value={field.help_text ?? ""}
              onChange={(e) => onChange({ help_text: e.target.value || null })}
            />
            <p className="rf-prop-hint">
              Shown beneath the field to guide the person filling it in.
            </p>
          </div>
          <div className="rf-prop-field">
            <label className="rf-prop-label">Section</label>
            <select
              className="rf-prop-select"
              value={field.section_id ?? ""}
              onChange={(e) => onChange({ section_id: e.target.value || null })}
            >
              <option value="">Ungrouped</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Layout — field width (§6.1) */}
      {!isSection && (
        <>
          <div className="rf-prop-grouptitle">Layout</div>
          <div className="rf-prop-field">
            <label className="rf-prop-label">Field width</label>
            <WidthSeg value={field.width ?? "full"} onChange={(w) => onChange({ width: w })} />
            <p className="rf-prop-hint">
              Set <b>Half</b> and the field pairs up with the next half-width field
              — the two-column layout shown on the live form.
            </p>
          </div>
        </>
      )}

      {/* Behaviour — Required + Conditional display (amber rule builder) */}
      {!isSection && (
        <>
          <div className="rf-prop-grouptitle">Behaviour</div>
          <div
            className={`rf-prop-toggle${field.required ? " on" : ""}`}
            onClick={() => onChange({ required: !field.required })}
          >
            <div>
              <div className="lbl">Required</div>
              <div className="sub">Must be filled before submitting</div>
            </div>
            <span className="rf-mini-toggle" />
          </div>

          <div
            className={`rf-prop-toggle${field.show_when ? " on" : ""}`}
            onClick={() => {
              if (field.show_when) onChange({ show_when: null });
              else {
                const seed = seedExpr(triggers);
                if (seed) onChange({ show_when: seed });
              }
            }}
          >
            <div>
              <div className="lbl">Conditional display</div>
              <div className="sub">Only show when a rule is met</div>
            </div>
            <span className="rf-mini-toggle" />
          </div>
          {field.show_when && (
            <ConditionalSection
              lead="Show"
              fieldLabel={field.label}
              value={field.show_when}
              triggers={triggers}
              onChange={(v) => onChange({ show_when: v })}
            />
          )}

          <div
            className={`rf-prop-toggle${field.required_when ? " on" : ""}`}
            style={{ marginTop: 8 }}
            onClick={() => {
              if (field.required_when) onChange({ required_when: null });
              else {
                const seed = seedExpr(triggers);
                if (seed) onChange({ required_when: seed });
              }
            }}
          >
            <div>
              <div className="lbl">Conditional requirement</div>
              <div className="sub">Required only when a rule is met</div>
            </div>
            <span className="rf-mini-toggle" />
          </div>
          {field.required_when && (
            <ConditionalSection
              lead="Require"
              fieldLabel={field.label}
              value={field.required_when}
              triggers={triggers}
              onChange={(v) => onChange({ required_when: v })}
            />
          )}
        </>
      )}

      {/* Validation */}
      {!isSection && (field.type === "short_text" || field.type === "long_text") && (
        <>
          <div className="rf-prop-grouptitle">Validation</div>
          <NumberProp
            label="Min length"
            value={field.min_length ?? null}
            onChange={(v) => onChange({ min_length: v })}
          />
          <NumberProp
            label="Max length"
            value={field.max_length ?? null}
            onChange={(v) => onChange({ max_length: v })}
          />
          <div className="rf-prop-field">
            <label className="rf-prop-label">Regex pattern</label>
            <input
              className="rf-prop-input"
              value={field.regex ?? ""}
              onChange={(e) => onChange({ regex: e.target.value || null })}
            />
          </div>
        </>
      )}

      {!isSection && field.type === "number" && (
        <>
          <div className="rf-prop-grouptitle">Validation</div>
          <NumberProp
            label="Min value"
            value={field.min_value ?? null}
            onChange={(v) => onChange({ min_value: v })}
          />
          <NumberProp
            label="Max value"
            value={field.max_value ?? null}
            onChange={(v) => onChange({ max_value: v })}
          />
        </>
      )}

      {field.type === "file_upload" && (
        <>
          <div className="rf-prop-grouptitle">Upload limits</div>
          <NumberProp
            label="Max files"
            value={field.max_files ?? 1}
            min={1}
            onChange={(v) => onChange({ max_files: Math.max(1, v ?? 1) })}
          />
          <NumberProp
            label="Max file size (MB)"
            value={field.max_file_size_mb ?? 10}
            min={1}
            onChange={(v) => onChange({ max_file_size_mb: Math.max(1, v ?? 10) })}
          />
        </>
      )}

      {/* Autotask mapping */}
      {!isSection && (
        <>
          <div className="rf-prop-grouptitle">Autotask mapping</div>
          <div className="rf-prop-field">
            <label className="rf-prop-label">Map to Autotask field</label>
            <select
              className="rf-prop-select"
              value={mapping?.target ?? ""}
              onChange={(e) => {
                const target = e.target.value;
                if (!target) {
                  onChange({ autotask_mapping: null });
                } else if (target === "udf") {
                  onChange({
                    autotask_mapping: { target: "udf", udf_name: mapping?.udf_name ?? "" },
                  });
                } else {
                  onChange({
                    autotask_mapping: {
                      target: target as NonNullable<
                        FormFieldSchema["autotask_mapping"]
                      >["target"],
                    },
                  });
                }
              }}
            >
              <option value="">— none —</option>
              {autotaskFields.map((t) => (
                <option key={t.target} value={t.target}>
                  {t.label}
                </option>
              ))}
            </select>
            {autotaskDegraded && (
              <p className="rf-prop-hint">Sync Autotask to see picklist targets.</p>
            )}
          </div>

          {mapping?.target === "udf" && (
            <div className="rf-prop-field">
              <label className="rf-prop-label">UDF name</label>
              <input
                className="rf-prop-input"
                value={mapping.udf_name ?? ""}
                onChange={(e) =>
                  onChange({ autotask_mapping: { target: "udf", udf_name: e.target.value } })
                }
              />
            </div>
          )}

          {mapping && (
            <div className="rf-mapping">
              <div className="rf-mapping-row">
                <span className="k">Maps to</span>
                <span className="v">
                  {mapping.target === "udf"
                    ? `udf:${mapping.udf_name ?? ""}`
                    : selectedTarget?.target ?? mapping.target}
                </span>
              </div>
            </div>
          )}
          <p className="rf-prop-hint">
            When submitted, this value is written to the mapped Autotask field on
            the new ticket.
          </p>
        </>
      )}

      {/* Stable id */}
      <p className="rf-prop-hint" style={{ marginTop: 18 }}>
        id: {field.id} — don&apos;t change once published.
      </p>
    </div>
  );
}

function WidthSeg({
  value,
  onChange,
}: {
  value: "half" | "full";
  onChange: (w: "half" | "full") => void;
}) {
  return (
    <div className="rf-wseg">
      <button
        type="button"
        className={value === "half" ? "on" : ""}
        onClick={() => onChange("half")}
      >
        <span className="pv">
          <i className="lead" />
          <i />
        </span>
        Half
      </button>
      <button
        type="button"
        className={value === "full" ? "on" : ""}
        onClick={() => onChange("full")}
      >
        <span className="pv">
          <i className="lead" />
        </span>
        Full
      </button>
    </div>
  );
}

function NumberProp({
  label,
  value,
  min,
  onChange,
}: {
  label: string;
  value: number | null;
  min?: number;
  onChange: (v: number | null) => void;
}) {
  return (
    <div className="rf-prop-field">
      <label className="rf-prop-label">{label}</label>
      <input
        className="rf-prop-input"
        type="number"
        min={min}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    </div>
  );
}

function OptionsEditor({
  options,
  fieldType,
  defaultValue,
  onChange,
}: {
  options: FormFieldOption[];
  fieldType: FormFieldType;
  defaultValue: string | string[] | null;
  // Always emits both options and the (kept-in-sync) default together so the
  // pair lands in a single schema commit — two separate updates would race on
  // the same stale snapshot and the second would clobber the first.
  onChange: (opts: FormFieldOption[], next: string | string[] | null) => void;
}) {
  // Multi-choice fields pre-select a set; single-choice fields pre-select one.
  const isMulti = fieldType === "multi_select" || fieldType === "checkbox_group";
  const defaultList = Array.isArray(defaultValue) ? defaultValue : [];
  const defaultSingle = typeof defaultValue === "string" ? defaultValue : "";
  const currentDefault = (): string | string[] | null =>
    isMulti ? (defaultList.length ? defaultList : null) : defaultSingle || null;
  const isDefault = (value: string) =>
    isMulti ? defaultList.includes(value) : defaultSingle === value;

  function toggleDefault(value: string) {
    if (isMulti) {
      const next = defaultList.includes(value)
        ? defaultList.filter((v) => v !== value)
        : [...defaultList, value];
      onChange(options, next.length ? next : null);
    } else {
      onChange(options, defaultSingle === value ? null : value);
    }
  }

  function update(i: number, patch: Partial<FormFieldOption>) {
    const prev = options[i]?.value;
    const nextOpts = options.map((o, idx) => (idx === i ? { ...o, ...patch } : o));
    // Re-point the default if this option's stored value was renamed.
    let nextDefault = currentDefault();
    if (patch.value !== undefined && prev !== undefined && patch.value !== prev) {
      if (isMulti) {
        if (defaultList.includes(prev)) {
          nextDefault = defaultList.map((v) => (v === prev ? patch.value! : v));
        }
      } else if (defaultSingle === prev) {
        nextDefault = patch.value || null;
      }
    }
    onChange(nextOpts, nextDefault);
  }
  function add() {
    onChange(
      [
        ...options,
        { value: `opt${options.length + 1}`, label: `Option ${options.length + 1}` },
      ],
      currentDefault(),
    );
  }
  function remove(i: number) {
    const removed = options[i]?.value;
    const nextOpts = options.filter((_, idx) => idx !== i);
    let nextDefault = currentDefault();
    if (removed !== undefined) {
      if (isMulti && defaultList.includes(removed)) {
        const kept = defaultList.filter((v) => v !== removed);
        nextDefault = kept.length ? kept : null;
      } else if (!isMulti && defaultSingle === removed) {
        nextDefault = null;
      }
    }
    onChange(nextOpts, nextDefault);
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= options.length) return;
    const nextOpts = options.slice();
    [nextOpts[i], nextOpts[j]] = [nextOpts[j], nextOpts[i]];
    // Reorder only; the default is keyed by option value, so it is unaffected.
    onChange(nextOpts, currentDefault());
  }
  return (
    <div>
      <p className="rf-prop-hint" style={{ marginTop: 0, marginBottom: 8 }}>
        &ldquo;Label&rdquo; is what users see; &ldquo;value&rdquo; is stored with
        the submission. Tap the circle to pre-select{" "}
        {isMulti ? "options" : "an option"} when the form opens.
      </p>
      <div className="rf-opt-list">
        <div className="rf-opt-head" aria-hidden="true">
          <span className="sp sp-def" />
          <span className="h-label">Label</span>
          <span className="h-value">Value</span>
          <span className="sp sp-mv" />
          <span className="sp sp-rm" />
        </div>
        {options.map((o, i) => (
          <div key={i} className="rf-opt">
            <button
              type="button"
              className={`rf-opt-def${isMulti ? " sq" : ""}${isDefault(o.value) ? " on" : ""}`}
              aria-pressed={isDefault(o.value)}
              title={isDefault(o.value) ? "Default selection" : "Set as default"}
              onClick={() => toggleDefault(o.value)}
            >
              {isDefault(o.value) && <Icon name="check" size={12} />}
            </button>
            <input
              placeholder="Label"
              aria-label="Option label (shown to users)"
              value={o.label}
              onChange={(e) => update(i, { label: e.target.value })}
            />
            <input
              placeholder="Value"
              aria-label="Option value (stored with the submission)"
              value={o.value}
              onChange={(e) => update(i, { value: e.target.value })}
            />
            <button
              type="button"
              className="mv"
              disabled={i === 0}
              onClick={() => move(i, -1)}
              aria-label="Move option up"
              title="Move up"
            >
              <Icon name="chevUp" size={14} />
            </button>
            <button
              type="button"
              className="mv"
              disabled={i === options.length - 1}
              onClick={() => move(i, 1)}
              aria-label="Move option down"
              title="Move down"
            >
              <Icon name="chevDown" size={14} />
            </button>
            <button type="button" className="rm" onClick={() => remove(i)} aria-label="Remove option">
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="rf-add-opt" onClick={add}>
        <Icon name="plus" size={13} /> Add option
      </button>
    </div>
  );
}

// ---- Conditional rule builder (amber) — maps to ConditionalExpression ------

interface Trigger {
  id: string;
  label: string;
  multi: boolean;
  /** A single checkbox: its value is a boolean, not one of `options`. */
  bool: boolean;
  options: FormFieldOption[];
}

type SimpleOp = "is" | "is-not" | "includes" | "excludes" | "count_gt" | "count_lt" | "count_eq";
interface SimpleRule {
  fieldId: string;
  op: SimpleOp;
  value: string;
}
interface SimpleModel {
  match: "all" | "any";
  rules: SimpleRule[];
}

const OP_LABEL: Record<SimpleOp, string> = {
  is: "is",
  "is-not": "is not",
  includes: "includes",
  excludes: "doesn't include",
  count_gt: "count is more than",
  count_lt: "count is less than",
  count_eq: "count is exactly",
};

const SINGLE_OPS: Array<[SimpleOp, string]> = [
  ["is", "is"],
  ["is-not", "is not"],
];
const MULTI_OPS: Array<[SimpleOp, string]> = [
  ["includes", "includes"],
  ["excludes", "does not include"],
];
const COUNT_OPS: Array<[SimpleOp, string]> = [
  ["count_gt", "count is more than"],
  ["count_lt", "count is less than"],
  ["count_eq", "count is exactly"],
];

/** Parse a leaf (or NOT-of-leaf) ConditionalExpression into a simple rule. */
function parseLeaf(node: ConditionalExpression | undefined | null): SimpleRule | null {
  if (!node) return null;
  if (node.type === "field_eq")
    return { fieldId: node.field_id, op: "is", value: String(node.value ?? "") };
  if (node.type === "field_in")
    return {
      fieldId: node.field_id,
      op: "includes",
      value: String((node.values ?? [])[0] ?? ""),
    };
  if (node.type === "field_len_gt")
    return { fieldId: node.field_id, op: "count_gt", value: String(node.value ?? "") };
  if (node.type === "field_len_lt")
    return { fieldId: node.field_id, op: "count_lt", value: String(node.value ?? "") };
  if (node.type === "field_len_eq")
    return { fieldId: node.field_id, op: "count_eq", value: String(node.value ?? "") };
  if (node.type === "not") {
    const inner = node.children[0];
    if (inner?.type === "field_eq")
      return { fieldId: inner.field_id, op: "is-not", value: String(inner.value ?? "") };
    if (inner?.type === "field_in")
      return {
        fieldId: inner.field_id,
        op: "excludes",
        value: String((inner.values ?? [])[0] ?? ""),
      };
  }
  return null;
}

/** Parse a ConditionalExpression into the builder's simple model (or null). */
function exprToModel(expr: ConditionalExpression | null | undefined): SimpleModel | null {
  if (!expr) return null;
  if (expr.type === "and" || expr.type === "or") {
    const rules = expr.children.map(parseLeaf);
    if (rules.some((r) => r === null)) return null;
    return { match: expr.type === "and" ? "all" : "any", rules: rules as SimpleRule[] };
  }
  const single = parseLeaf(expr);
  return single ? { match: "all", rules: [single] } : null;
}

function isBoolTrigger(triggers: Trigger[], fieldId: string): boolean {
  return triggers.find((t) => t.id === fieldId)?.bool ?? false;
}

function ruleToExpr(r: SimpleRule, isBool: boolean): ConditionalExpression {
  // Checkbox fields submit a boolean, and the runtime compares with strict
  // equality, so the rule value must be a real `true`, not the string "true".
  const value: unknown = isBool ? r.value === "true" : r.value;
  switch (r.op) {
    case "is":
      return { type: "field_eq", field_id: r.fieldId, value };
    case "is-not":
      return { type: "not", children: [{ type: "field_eq", field_id: r.fieldId, value }] };
    case "includes":
      return { type: "field_in", field_id: r.fieldId, values: [value] };
    case "excludes":
      return {
        type: "not",
        children: [{ type: "field_in", field_id: r.fieldId, values: [value] }],
      };
    case "count_gt":
      return { type: "field_len_gt", field_id: r.fieldId, value: Number(r.value) };
    case "count_lt":
      return { type: "field_len_lt", field_id: r.fieldId, value: Number(r.value) };
    case "count_eq":
      return { type: "field_len_eq", field_id: r.fieldId, value: Number(r.value) };
  }
}

function modelToExpr(model: SimpleModel, triggers: Trigger[]): ConditionalExpression | null {
  if (model.rules.length === 0) return null;
  const toExpr = (r: SimpleRule) => ruleToExpr(r, isBoolTrigger(triggers, r.fieldId));
  if (model.rules.length === 1) return toExpr(model.rules[0]);
  return {
    type: model.match === "all" ? "and" : "or",
    children: model.rules.map(toExpr),
  };
}

function defaultRule(triggers: Trigger[]): SimpleRule | null {
  const t = triggers[0];
  if (!t) return null;
  return { fieldId: t.id, op: t.multi ? "includes" : "is", value: t.options[0]?.value ?? "" };
}

function seedExpr(triggers: Trigger[]): ConditionalExpression | null {
  const r = defaultRule(triggers);
  return r ? ruleToExpr(r, isBoolTrigger(triggers, r.fieldId)) : null;
}

/**
 * Wraps the amber RuleBuilder, falling back gracefully when (a) there are no
 * eligible trigger fields, or (b) the stored expression is an advanced rule the
 * visual builder can't represent (e.g. numeric > / < or nested logic edited via
 * JSON) — which we must not silently clobber.
 */
function ConditionalSection({
  lead,
  fieldLabel,
  value,
  triggers,
  onChange,
}: {
  lead: "Show" | "Require";
  fieldLabel: string;
  value: ConditionalExpression;
  triggers: Trigger[];
  onChange: (v: ConditionalExpression | null) => void;
}) {
  if (!exprToModel(value)) {
    return (
      <div className="rf-cond-builder">
        <p style={{ fontSize: 12, color: "var(--iq-text-muted)", margin: 0 }}>
          This field uses an advanced rule edited directly in the JSON schema.
          Toggle this off to replace it with the visual builder.
        </p>
      </div>
    );
  }
  if (triggers.length === 0) {
    return (
      <div className="rf-cond-builder">
        <p style={{ fontSize: 12, color: "var(--iq-text-muted)", margin: 0 }}>
          Add a choice field (dropdown, radio, or checkbox group) to this form
          to drive a rule.
        </p>
      </div>
    );
  }
  return (
    <RuleBuilder
      lead={lead}
      fieldLabel={fieldLabel}
      value={value}
      triggers={triggers}
      onChange={onChange}
    />
  );
}

function RuleBuilder({
  lead,
  fieldLabel,
  value,
  triggers,
  onChange,
}: {
  lead: "Show" | "Require";
  fieldLabel: string;
  value: ConditionalExpression;
  triggers: Trigger[];
  onChange: (v: ConditionalExpression | null) => void;
}) {
  // Controlled: derive the model from the persisted expr, fall back to a seed.
  const model = exprToModel(value) ?? {
    match: "all" as const,
    rules: defaultRule(triggers) ? [defaultRule(triggers)!] : [],
  };

  function push(next: SimpleModel) {
    onChange(modelToExpr(next, triggers));
  }

  function setMatch(match: "all" | "any") {
    push({ ...model, match });
  }
  function addCondition() {
    const r = defaultRule(triggers);
    if (r) push({ ...model, rules: [...model.rules, r] });
  }
  function removeCondition(i: number) {
    push({ ...model, rules: model.rules.filter((_, idx) => idx !== i) });
  }
  function setRule(i: number, patch: Partial<SimpleRule>) {
    push({
      ...model,
      rules: model.rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    });
  }
  function setRuleField(i: number, fieldId: string) {
    const t = triggers.find((x) => x.id === fieldId);
    if (!t) return;
    setRule(i, {
      fieldId,
      op: t.multi ? "includes" : "is",
      value: t.options[0]?.value ?? "",
    });
  }
  function setRuleOp(i: number, op: SimpleOp) {
    const r = model.rules[i];
    const toCount = op.startsWith("count_");
    const wasCount = r.op.startsWith("count_");
    // Crossing the count/membership boundary changes what `value` means
    // (a numeric threshold vs an option value), so reset it to a sane default.
    if (toCount === wasCount) {
      setRule(i, { op });
      return;
    }
    const trig = triggers.find((t) => t.id === r.fieldId);
    setRule(i, { op, value: toCount ? "1" : trig?.options[0]?.value ?? "" });
  }

  return (
    <div className="rf-cond-builder">
      <div className="rf-cond-when">
        {/* ALL vs ANY only carries meaning with 2+ conditions; with a single
            condition the two are identical and the choice can't be persisted
            (the expression stores no and/or wrapper), so hide the toggle. */}
        {model.rules.length >= 2 ? (
          <>
            <span className="lead">{lead} this field when</span>
            <span className="rf-cond-match">
              <button
                type="button"
                className={model.match === "all" ? "sel" : ""}
                onClick={() => setMatch("all")}
              >
                ALL
              </button>
              <button
                type="button"
                className={model.match === "any" ? "sel" : ""}
                onClick={() => setMatch("any")}
              >
                ANY
              </button>
            </span>
            <span>of these match:</span>
          </>
        ) : (
          <span className="lead">{lead} this field when:</span>
        )}
      </div>

      <div className="rf-cond-rows">
        {model.rules.map((r, i) => {
          const trig = triggers.find((t) => t.id === r.fieldId);
          // Multi fields can be matched by membership (includes/excludes) or by
          // how many options are ticked (count_*); single fields only by value.
          const ops = trig?.multi ? [...MULTI_OPS, ...COUNT_OPS] : SINGLE_OPS;
          return (
            <div className="rf-cond-row" key={i}>
              {i > 0 && (
                <span className="rf-cond-row-conj">
                  {model.match === "all" ? "AND" : "OR"}
                </span>
              )}
              <div className="rf-cond-line">
                <select
                  className="rf-cs"
                  value={r.fieldId}
                  onChange={(e) => setRuleField(i, e.target.value)}
                >
                  {triggers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rf-cond-line2">
                <select
                  className="rf-cs"
                  value={r.op}
                  onChange={(e) => setRuleOp(i, e.target.value as SimpleOp)}
                >
                  {ops.map(([op, label]) => (
                    <option key={op} value={op}>
                      {label}
                    </option>
                  ))}
                </select>
                {r.op.startsWith("count_") ? (
                  <input
                    type="number"
                    className="rf-cs"
                    value={r.value}
                    onChange={(e) => setRule(i, { value: e.target.value })}
                    placeholder="0"
                    min="0"
                  />
                ) : (
                  <select
                    className="rf-cs"
                    value={r.value}
                    onChange={(e) => setRule(i, { value: e.target.value })}
                  >
                    {(trig?.options ?? []).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {i > 0 && (
                <button
                  type="button"
                  className="rf-cond-remove"
                  title="Remove condition"
                  onClick={() => removeCondition(i)}
                >
                  <Icon name="x" size={13} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <button type="button" className="rf-cond-add" onClick={addCondition}>
        <Icon name="plus" size={13} /> Add condition
      </button>

      <div className="rf-cond-preview">
        <span className="lbl">Result</span>
        {lead} <b className="fld">{fieldLabel}</b> only when{" "}
        {model.rules.map((r, i) => {
          const trig = triggers.find((t) => t.id === r.fieldId);
          const isCountOp = r.op.startsWith("count_");
          const label = isCountOp ? r.value : trig?.options.find((o) => o.value === r.value)?.label ?? r.value;
          return (
            <span key={i}>
              {i > 0 && <b> {model.match === "all" ? "and" : "or"} </b>}
              <b className="fld">{trig?.label ?? r.fieldId}</b> {OP_LABEL[r.op]}{" "}
              <b className="val">{label}</b>
            </span>
          );
        })}
        .
      </div>
    </div>
  );
}

// ---- Preview modal ---------------------------------------------------------

function PreviewModal({
  schema,
  peopleCompanyId,
  onClose,
}: {
  schema: FormSchemaShape;
  // For user_picker fields: when the form is assigned to a single client,
  // preview its people directory so iQuest staff see what that client will see
  // (issue #29). null = the caller's own company (the default).
  peopleCompanyId?: string | null;
  onClose: () => void;
}) {
  const panelRef = useModalDismiss(onClose);
  const [values, setValues] = useState<Record<string, unknown>>(() =>
    initialFormValues(schema.fields),
  );
  const sections = schema.sections ?? [];

  const fieldsBySection = useMemo(() => {
    const map = new Map<string, FormFieldSchema[]>();
    map.set(UNGROUPED, []);
    for (const s of sections) map.set(s.id, []);
    for (const f of schema.fields) {
      const key = f.section_id && map.has(f.section_id) ? f.section_id : UNGROUPED;
      map.get(key)!.push(f);
    }
    return map;
  }, [schema.fields, sections]);

  function renderFields(fields: FormFieldSchema[]) {
    const visible = fields.filter((f) => evalCondition(f.show_when ?? null, values));
    if (visible.length === 0) return null;
    return (
      <div className="rf-fieldgrid">
        {visible.map((field) => (
          <FormFieldRenderer
            key={field.id}
            field={field}
            value={values[field.id]}
            onChange={(v) => setValues((prev) => ({ ...prev, [field.id]: v }))}
            peopleCompanyId={peopleCompanyId}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-panel modal-panel-lg"
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">{schema.title || "Preview"}</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {schema.description && <p className="form-field-help">{schema.description}</p>}
          {sections.map((s) => {
            const fields = fieldsBySection.get(s.id) ?? [];
            return (
              <div key={s.id} style={{ marginBottom: 16 }}>
                <h3 className="form-section-title">{s.title}</h3>
                {s.description && <p className="form-section-blurb">{s.description}</p>}
                {renderFields(fields)}
              </div>
            );
          })}
          {renderFields(fieldsBySection.get(UNGROUPED) ?? [])}
          {schema.fields.length === 0 && (
            <p className="empty-state-blurb">This form has no fields yet.</p>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-primary" disabled>
            {schema.submit_label ?? "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Copy-fields-from-another-form modal -----------------------------------

function CopyFieldsModal({
  forms,
  currentId,
  onCopy,
  onClose,
}: {
  forms: FormAdmin[];
  currentId: string;
  onCopy: (source: FormAdmin) => void;
  onClose: () => void;
}) {
  const panelRef = useModalDismiss(onClose);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const others = forms.filter((f) => f.id !== currentId);
  const filtered = q
    ? others.filter(
        (f) =>
          f.title.toLowerCase().includes(q) ||
          (f.client_facing_title ?? "").toLowerCase().includes(q) ||
          f.slug.toLowerCase().includes(q),
      )
    : others;

  const fieldCountOf = (f: FormAdmin): number =>
    (f.draft_schema ?? f.published_schema)?.fields.length ?? 0;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-panel"
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">Copy fields from another form</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <p className="form-field-help">
            Pick a form to copy its fields and sections into this one. Your
            existing fields are kept; the copied fields are appended with fresh
            ids so nothing collides. Nothing is saved until you Save draft.
          </p>
          <div className="rf-list-search" style={{ marginBottom: 8 }}>
            <Icon name="search" />
            <input
              type="text"
              placeholder="Search by IT or client-facing name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          {filtered.map((f) => {
            const count = fieldCountOf(f);
            const shape = f.draft_schema ?? f.published_schema ?? null;
            const color: FormIconColor = shape?.icon_color ?? "violet";
            return (
              <button
                key={f.id}
                type="button"
                className="rf-formitem"
                style={{
                  width: "100%",
                  textAlign: "left",
                  opacity: count === 0 ? 0.5 : 1,
                }}
                disabled={count === 0}
                onClick={() => {
                  onCopy(f);
                  onClose();
                }}
              >
                <span className={`rf-formitem-icon ${color}`}>
                  <Icon name={formListIcon(f)} size={17} />
                </span>
                <span className="rf-formitem-text">
                  <span className="rf-formitem-name">{f.title}</span>
                  {f.client_facing_title ? (
                    <span className="rf-formitem-client">
                      <Icon name="eye" size={12} />
                      {f.client_facing_title}
                    </span>
                  ) : (
                    <span className="rf-formitem-meta">/{f.slug}</span>
                  )}
                </span>
                <span className="rf-scope-badge">
                  {count} field{count === 1 ? "" : "s"}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="empty-state-blurb" style={{ padding: "12px 4px" }}>
              {others.length === 0
                ? "There are no other forms to copy from yet."
                : "No forms match your search."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Helpers ---------------------------------------------------------------

// Re-point a conditional rule's field references at the cloned field ids when
// copying fields between forms. Unknown ids pass through unchanged (the rule
// stays inert rather than throwing).
function remapConditionFieldIds(
  expr: ConditionalExpression | null | undefined,
  idMap: Map<string, string>,
): ConditionalExpression | null {
  if (!expr) return null;
  switch (expr.type) {
    case "and":
    case "or":
      return {
        type: expr.type,
        children: expr.children.map((c) => remapConditionFieldIds(c, idMap)!),
      };
    case "not":
      return {
        type: "not",
        children: [remapConditionFieldIds(expr.children[0], idMap)!],
      };
    case "field_eq":
      return { ...expr, field_id: idMap.get(expr.field_id) ?? expr.field_id };
    case "field_in":
      return {
        ...expr,
        field_id: idMap.get(expr.field_id) ?? expr.field_id,
        values: [...expr.values],
      };
    case "field_gt":
    case "field_lt":
    case "field_len_gt":
    case "field_len_lt":
    case "field_len_eq":
      return { ...expr, field_id: idMap.get(expr.field_id) ?? expr.field_id };
  }
}

function scopeBadge(f: FormAdmin): string {
  if (f.scope === "global") return "All";
  if (f.scope === "multi_company") {
    const n = f.audience_company_ids.length;
    return `${n} client${n === 1 ? "" : "s"}`;
  }
  return "1 client";
}

function needsOptions(type: FormFieldType): boolean {
  return (
    type === "single_select" ||
    type === "multi_select" ||
    type === "checkbox_group" ||
    type === "radio_group"
  );
}

function defaultLabel(type: FormFieldType): string {
  switch (type) {
    case "short_text":
      return "Short text";
    case "long_text":
      return "Long text";
    case "number":
      return "Number";
    case "date":
      return "Date";
    case "datetime":
      return "Date + time";
    case "single_select":
      return "Choose one";
    case "multi_select":
      return "Choose many";
    case "checkbox_group":
      return "Choose multiple";
    case "single_checkbox":
      return "Confirm";
    case "radio_group":
      return "Pick one";
    case "file_upload":
      return "Attachments";
    case "user_picker":
      return "User";
    case "section_header":
      return "Section";
  }
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `fld_${Math.random().toString(36).slice(2, 10)}`;
}

// ---- Inline SVG glyphs (Lucide-style, currentColor) ------------------------

function svg(children: React.ReactNode) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  );
}

function gripIcon() {
  return svg(
    <>
      <circle cx="9" cy="6" r="1" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="9" cy="18" r="1" />
      <circle cx="15" cy="6" r="1" />
      <circle cx="15" cy="12" r="1" />
      <circle cx="15" cy="18" r="1" />
    </>,
  );
}

function trashIcon() {
  return svg(
    <>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14" />
    </>,
  );
}

// Field-width glyphs (16x12): one full bar vs. two half bars (lead emphasised).
function widthIcon(kind: "half" | "full") {
  if (kind === "half") {
    return (
      <svg viewBox="0 0 16 12" fill="currentColor">
        <rect x="1" y="2" width="6" height="8" rx="1.5" />
        <rect x="9" y="2" width="6" height="8" rx="1.5" opacity="0.38" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 16 12" fill="currentColor">
      <rect x="1" y="2" width="14" height="8" rx="1.5" />
    </svg>
  );
}

function iconForType(type: FormFieldType) {
  switch (type) {
    case "short_text":
      return svg(<path d="M4 7h16M4 12h10M4 17h7" />);
    case "long_text":
      return svg(<path d="M4 6h16M4 10h16M4 14h16M4 18h10" />);
    case "number":
      return svg(
        <>
          <path d="M9 4v16M15 4v16M5 9h14M5 15h14" />
        </>,
      );
    case "date":
      return svg(
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </>,
      );
    case "datetime":
      return svg(
        <>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M3 10h18M12 14v3l2 1" />
        </>,
      );
    case "single_select":
      return svg(
        <>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M8 11l4 4 4-4" />
        </>,
      );
    case "multi_select":
      return svg(
        <>
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </>,
      );
    case "radio_group":
      return svg(
        <>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3" />
        </>,
      );
    case "checkbox_group":
      return svg(
        <>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M8 12l3 3 5-6" />
        </>,
      );
    case "single_checkbox":
      return svg(
        <>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="M8 12l3 3 5-6" />
        </>,
      );
    case "file_upload":
      return svg(
        <>
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <path d="M7 10l5-5 5 5M12 5v12" />
        </>,
      );
    case "user_picker":
      return svg(
        <>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
        </>,
      );
    case "section_header":
      return svg(<path d="M4 6h16M4 12h16M4 18h16" />);
  }
}
